import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getConditionById from 'editor/src/store/design/selector/getConditionById';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { MediaText } from 'editor/src/store/design/types';
import resolveTextVariant from 'editor/src/store/design/util/resolveTextVariant';
import updateTextPropertiesWithoutRender from 'editor/src/store/design/util/updateTextPropertiesWithoutRender';
import loadFontOperation from 'editor/src/store/fonts/operation/loadFontOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import limitPrecision from 'editor/src/util/limitPrecision';

import updateHiddenElementsOperation from './updateHiddenElementsOperation';

/**
 * Re-measures every element that varies with `conditionId` and now renders in
 * `fontFamily`, then re-packs the spreads whose geometry actually moved.
 *
 * Needed because a variant may name a font the browser does not have yet, and
 * measuring is only possible once it does: `extractTextProperties` keeps the
 * element's PREVIOUS width when `isFontFileLoaded` is false. Without this pass
 * a swap to a not-yet-fetched face (the normal case — only the active option's
 * font is preloaded) leaves the reflow row packed at the outgoing language's
 * widths for good, because nothing else re-measures when a font lands.
 *
 * A no-op when nothing the measure produces has moved — which covers the
 * font-was-loaded case. "Nothing moved" means the box AND the fitted font size:
 * an auto-fit label keeps its authored box and adapts the font instead, so the
 * box alone is not enough to tell.
 */
export const remeasureVariantsInFontOperation =
  (conditionId: string, fontFamily: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const spreads = getState().design.designData?.spreads;
    if (!spreads) {
      return;
    }

    const affectedSpreads = new Set<number>();

    batch(() => {
      spreads.forEach((spread, spreadIndex) => {
        spread.pages[0]?.groups.media?.forEach((element, elementIndex) => {
          if (
            element.type !== 'text' ||
            element.extra.textVariants?.conditionId !== conditionId ||
            element.extra.fontFamily !== fontFamily
          ) {
            return;
          }

          const elementUpdate: Partial<MediaText> = { extra: { ...element.extra } };
          updateTextPropertiesWithoutRender(element, elementUpdate, undefined);
          // fontSize counts as much as the box: an auto-fit text
          // (TextWrapping.Fit / FitOneLine) is measured INSIDE a fixed box, so
          // extractTextProperties hands back the authored width and height
          // unchanged and shrinks the font to make the glyphs fit. Comparing
          // the box alone would call every such label unchanged and throw away
          // the only number the new face actually moved — leaving a CJK label
          // at the outgoing face's fitted size.
          //
          // To the same precision the box already gets. extractTextProperties
          // limitPrecisions width and height but not fontSize, which comes back
          // as mm2pt(pt2mm(size)) — a round trip that is NOT the identity in
          // IEEE754 for about one quarter-point size in seven (13 measures as
          // 12.999999999999998, 7 as 7.000000000000001). A text that is not
          // auto-fit never has its fitted size touched at all
          // (FabricPathText._wrapText returns early when `isFitting` is false),
          // so an exact compare would call those changed on every font load and
          // persist the drift into the design.
          const measuredFontSize = elementUpdate.extra?.fontSize;
          if (
            elementUpdate.width === element.width &&
            elementUpdate.height === element.height &&
            // No measurement is not the same as no movement, so it re-packs
            // rather than skipping. (`extra` is always assigned by the measure;
            // this is the type saying it need not be.)
            measuredFontSize !== undefined &&
            limitPrecision(measuredFontSize, 13) === limitPrecision(element.extra.fontSize, 13)
          ) {
            return;
          }

          dispatch(
            updateMediaElementAction({
              elementAddress: { spreadIndex, pageIndex: 0, elementIndex },
              elementUpdate,
            }),
          );
          affectedSpreads.add(spreadIndex);
        });
      });

      affectedSpreads.forEach((spreadIndex) => dispatch(updateHiddenElementsOperation(spreadIndex)));
    });
  };

/**
 * Swaps in the chosen option's text (and font, when the variant carries one)
 * on every element that varies with this condition — across ALL spreads, which
 * is what lets one design-scoped language dropdown drive both sides of a card.
 *
 * An option with no variant entry leaves the element as it was: the previous
 * language stays visible, so incomplete authoring shows up in the preview
 * instead of printing a silently blank label.
 *
 * Every touched spread gets a hidden/reflow recompute, because a swapped label
 * has a new width and its horizontal reflow row must re-pack around it. Returns
 * those spread indexes so the caller can skip recomputing them a second time.
 */
const applyTextVariantsForConditionOperation =
  (conditionId: string, optionId: string) =>
  (dispatch: ThunkDispatch, getState: () => RootState): Set<number> => {
    const affectedSpreads = new Set<number>();
    const state = getState();
    const spreads = state.design.designData?.spreads;
    if (!spreads) {
      return affectedSpreads;
    }
    // Looked up once, outside the per-element loop: every element varying by
    // this conditionId shares the same owning condition, and label mode needs
    // it to read the option's own text. Both real callers name a condition
    // that exists (see updateConditionActiveOptionOperation,
    // removeConditionOptionOperation), so this is normally defined — but it is
    // not required to be: resolveTextVariant degrades to per-option-only
    // resolution without it, which is the right answer for an orphaned
    // conditionId rather than silently doing nothing for every element.
    const condition = getConditionById(state, conditionId)?.condition;

    const introducedFonts = new Set<string>();

    batch(() => {
      spreads.forEach((spread, spreadIndex) => {
        spread.pages[0]?.groups.media?.forEach((element, elementIndex) => {
          if (element.type !== 'text' || element.extra.textVariants?.conditionId !== conditionId) {
            return;
          }
          // Its value is the code set's. No path pairs the two, so only imported
          // data gets here, and a picklist swap would print the option on the proof
          // while production prints the row.
          if (element.extra.variableColumn) {
            return;
          }
          const variant = resolveTextVariant(element.extra.textVariants, condition, optionId);
          if (!variant) {
            return;
          }

          const fontFamily = variant.fontFamily ?? element.extra.fontFamily;
          if (variant.text === element.extra.text && fontFamily === element.extra.fontFamily) {
            return;
          }

          const elementUpdate: Partial<MediaText> = {
            extra: { ...element.extra, text: variant.text, fontFamily },
          };
          // A swapped string invalidates the cached glyph outline exactly as a
          // canvas edit does (see updateMediaElementOperation); left in place
          // it keeps painting the outgoing language. Both faces are checked
          // because the font can travel with the variant.
          if (element.extra.fontFamily.startsWith('custom-') || fontFamily.startsWith('custom-')) {
            elementUpdate.path = undefined;
          }
          updateTextPropertiesWithoutRender(element, elementUpdate, undefined);
          dispatch(
            updateMediaElementAction({
              elementAddress: { spreadIndex, pageIndex: 0, elementIndex },
              elementUpdate,
            }),
          );

          if (fontFamily !== element.extra.fontFamily) {
            introducedFonts.add(fontFamily);
          }
          affectedSpreads.add(spreadIndex);
        });
      });

      affectedSpreads.forEach((spreadIndex) => dispatch(updateHiddenElementsOperation(spreadIndex)));
    });

    // Outside the batch: the measurement above used whatever face the browser
    // had at swap time, so re-measure once the real one lands. Resolves
    // immediately for an already-loaded font, where the re-measure is a no-op.
    introducedFonts.forEach((fontFamily) => {
      void Promise.resolve(dispatch(loadFontOperation(fontFamily))).then(() =>
        dispatch(remeasureVariantsInFontOperation(conditionId, fontFamily)),
      );
    });

    return affectedSpreads;
  };

export default applyTextVariantsForConditionOperation;
